
import Vue from 'vue';
import { mapGetters, mapState, mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	BalanceAccount,
	calcRunnitTokensDataForBalanceAccount,
	Team,
	hundredthsToTokens,
	tokensToHundredths,
} from '@run-diffusion/shared';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import RunnitTokens from '@/views/Runnits/RunnitTokens.vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins';
import { functions } from '@/firebase';
import ActionsIsland from '@/components/ActionsIsland.vue';
import NumberField from '@/components/base/NumberField.vue';
import TransferDirectionBtn from './TransferDirectionBtn.vue';

export default Vue.extend({
	name: 'TransferTokens',
	props: {
		value: { type: Boolean, default: false },
		sourceTeam: { type: Object, default: null },
	},
	data () {
		return {
			saving: false,

			transferToCompanyFromTeam: false,
			transferTokens: 0,

			sourceTeamBalanceAccount: null as BalanceAccount,
			loadingSourceTeamBalanceAccount: false,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		isSaveDisabled (): boolean {
			if (this.saving) return true;
			if (!(this.transferTokens > 0)) return true;
			if (!this.sourceTeam?.isActive) return true;

			if (this.transferToCompanyFromTeam) {
				return !this.sourceTeamHasTokens || this.isSourceTeamTransferringTooManyTokens;
			} else {
				return !this.companyHasTokens || this.isCompanyTransferringTooManyTokens;
			}
		},
		sourceTeamTokens (): number {
			if (!this.sourceTeam || !this.sourceTeamBalanceAccount || this.sourceTeamBalanceAccount.teamId !== this.sourceTeam.id) {
				return 0;
			}
			return calcRunnitTokensDataForBalanceAccount(this.sourceTeamBalanceAccount).runnitTokens;
		},
		/*
		The bank of the company this page shows, the figure its Company Token Bank card reads. The Teams
		table lists only that company's teams. Never sourceTeam.company: the table hands over a Team
		read fresh with .get(), where `company` is an unresolved DocumentReference, so every bank read
		as 0 and refused to transfer. The store's company is a string id until it has loaded.
		 */
		companyLoading (): boolean {
			return !this.team || typeof this.team.company === 'string';
		},
		companyTokens (): number {
			if (this.companyLoading || this.team.companyId !== this.sourceTeam?.companyId) return 0;
			return this.team.company?.runnitTokens || 0;
		},
		sourceTeamHasTokens (): boolean {
			return this.sourceTeamTokens > 0;
		},
		companyHasTokens (): boolean {
			return this.companyTokens > 0;
		},
		isSourceTeamTransferringTooManyTokens (): boolean {
			return this.transferToCompanyFromTeam && this.sourceTeamHasTokens && this.transferTokens > this.sourceTeamTokens;
		},
		isCompanyTransferringTooManyTokens (): boolean {
			return !this.transferToCompanyFromTeam && this.companyHasTokens && this.transferTokens > this.companyTokens;
		},
	},
	created () {
		this.bindSourceTeamBalanceAccount(this.sourceTeam);
	},
	watch: {
		sourceTeam: {
			immediate: false,
			async handler (newVal: Team, oldVal: Team) {
				const newTeamId: string = newVal?.id;
				const oldTeamId: string = oldVal?.id;
				if (newTeamId !== oldTeamId) {
					await this.bindSourceTeamBalanceAccount(newVal);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		hundredthsToTokens,
		tokensToHundredths,
		clearValues () {
			this.transferToCompanyFromTeam = false;
			this.transferTokens = 0;
		},
		setOpen (val: boolean) {
			if (!val) {
				this.clearValues();
			}
			this.$emit('input', val);
		},
		onTransferDirectionChange (val: boolean) {
			this.transferToCompanyFromTeam = !!val;
		},
		async bindSourceTeamBalanceAccount (sourceTeam: Team) {
			if (this.loadingSourceTeamBalanceAccount || !sourceTeam?.id) return;

			try {
				this.sourceTeamBalanceAccount = null;
				this.loadingSourceTeamBalanceAccount = true;
				await this.$bind(
					'sourceTeamBalanceAccount',
					db
						.collection('balanceAccounts')
						.doc(sourceTeam.id),
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding balance account data:', error);
			} finally {
				this.loadingSourceTeamBalanceAccount = false;
			}
		},
		async onSave () {
			if (this.transferToCompanyFromTeam && !this.sourceTeamHasTokens) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Selected ${this.displayLabels.team} has no tokens to transfer out`,
					show: true,
				});
				return;
			}
			if (!this.transferToCompanyFromTeam && !this.companyHasTokens) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Company has no tokens to transfer out',
					show: true,
				});
				return;
			}
			if (this.isSourceTeamTransferringTooManyTokens) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Cannot transfer more tokens than the selected ${this.displayLabels.team} has`,
					show: true,
				});
				return;
			}
			if (this.isCompanyTransferringTooManyTokens) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Cannot transfer more tokens than the company has`,
					show: true,
				});
				return;
			}
			if (
				(this.transferToCompanyFromTeam && !(this.transferTokens > 0)) ||
				(!this.transferToCompanyFromTeam && !(this.transferTokens > 0))
			) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Please enter in an amount of tokens to transfer`,
					show: true,
				});
				return;
			}

			this.saving = true;
			try {
				const functionRef = functions.httpsCallable('transferTeamTokens');
				const { success, message, errorMessage } = (await functionRef({
					sourceTeamId: this.sourceTeam.id,
					...(this.transferToCompanyFromTeam ? {
						tokensToCompany: this.transferTokens,
					} : {
						tokensFromCompany: this.transferTokens,
					}),
				})).data;

				if (!success) {
					if (errorMessage) {
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: errorMessage,
							show: true,
						});
					} else {
						throw new Error(message);
					}
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: 'Tokens transferred successfully',
						show: true,
					});
					this.$emit('success');
					this.setOpen(false);
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error transferring tokens, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
	},
	components: {
		NumberField,
		DialogOrBottomSheet,
		DialogContent,
		RunnitTokens,
		ActionsIsland,
		TransferDirectionBtn,
	},
});
